import React from 'react';
import styled from 'styled-components';
import {Detail, Icon} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import SetFeatureFlagAlwaysLast from '../../../../mutations/mission_control/set_feature_flag_always_last_mutation';
import {FEATURE_FLAG_CARD_BORDER, FEATURE_FLAG_NEUTRAL_FILL, featureFlagCardSurface} from '../featureFlagSurface';
import {RolloutDirectory} from '../rollout/featureFlagRolloutDirectory';
import {CompanyMembershipTable} from './CompanyMembershipTable';
import {CompanyPickerBar} from './CompanyPickerBar';
import {buildCompanyPickerOptions, buildMembershipRows, MembershipIndex} from './featureFlagLists';

const Card = styled.div`
	${featureFlagCardSurface}
`;

const InfoBanner = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 16px 18px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const InfoIcon = styled.span`
	flex: none;
	margin-top: 1px;
`;

const PICKER_LABEL = 'Add company to always-last list';

const BANNER_COPY =
	'One static list, used by every flag. These companies go to the end of the percentage fill order, so they only receive a flag at very high percentages. Typically the largest or most risk-averse accounts.';

export interface AlwaysLastTabProps {
	/** Encoded company ids currently on the always-last list. */
	alwaysLastCompanyIds: readonly string[];
	index: MembershipIndex;
	directory: RolloutDirectory;
}

/**
 * The one static, flag-independent always-last list: a company on it goes to the end of every flag's
 * percentage fill order. Add and remove both go through the same mutation — there is no separate
 * remove — distinguished only by the `alwaysLast` flag sent.
 */
export function AlwaysLastTab({alwaysLastCompanyIds, index, directory}: AlwaysLastTabProps): JSX.Element {
	const handleAddCompany = (companyId: string): void => {
		Util.CommitMutation(SetFeatureFlagAlwaysLast, {companyId, alwaysLast: true}, () => undefined);
	};

	const handleRemoveCompany = (companyId: string): void => {
		Util.CommitMutation(SetFeatureFlagAlwaysLast, {companyId, alwaysLast: false}, () => undefined);
	};

	return (
		<Card>
			<InfoBanner>
				<InfoIcon>
					<Icon icon="info" size="s" />
				</InfoIcon>
				<Detail color="medium">{BANNER_COPY}</Detail>
			</InfoBanner>
			<CompanyPickerBar
				label={PICKER_LABEL}
				options={buildCompanyPickerOptions(directory, new Set(alwaysLastCompanyIds))}
				onAdd={handleAddCompany}
			/>
			<CompanyMembershipTable
				rows={buildMembershipRows(alwaysLastCompanyIds, index, directory, {kind: 'alwaysLast'})}
				onRemove={handleRemoveCompany}
				emptyTitle="The always-last list is empty"
				emptyHint="Every eligible company is treated equally in the fill order."
			/>
		</Card>
	);
}
